// ===== SHARED SIDEBAR COLOR SYSTEM =====
export const sidebarColors = {
  light: {
    wrapperBg: "#f1f1f1ff",
    sidebarBg: "rgba(255, 255, 255, 0.95)",
    sidebarBorder: "rgba(0, 0, 0, 0.08)",
    text: "#0f172a",
    textSecondary: "#64748b",
    navHover: "rgba(59, 130, 246, 0.08)",
    navActive: "linear-gradient(135deg, #2e7cba, #2e7cba)",
    topbarBg: "rgba(255, 255, 255, 0.95)",
    contentBg: "transparent",
    inputBg: "rgba(248, 250, 252, 0.8)",
    inputBorder: "rgba(0, 0, 0, 0.12)",
    shadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
    glassEffect: "blur(16px)",
    notificationBg: "rgba(255, 255, 255, 0.98)",
    notificationShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
    modalBg: "rgba(255, 255, 255, 0.98)",
    modalOverlay: "rgba(0, 0, 0, 0.4)",
    userProfileBg: "rgba(255, 255, 255, 0.98)",
    userProfileShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
    statusOnline: "#22c55e",
    statusOffline: "#94a3b8",
    statusAway: "#f59e0b",
    statusBusy: "#ef4444",
    scrollTrack: "rgba(249, 250, 251, 0.8)",
    scrollThumb:
      "linear-gradient(135deg, rgba(59, 130, 246, 0.3), rgba(99, 102, 241, 0.2))",
    scrollThumbHover:
      "linear-gradient(135deg, rgba(59, 130, 246, 0.5), rgba(99, 102, 241, 0.4))",
    scrollThumbActive:
      "linear-gradient(135deg, rgba(59, 130, 246, 0.7), rgba(99, 102, 241, 0.6))",
  },
  dark: {
    wrapperBg: "rgba(15, 23, 42, 0.95)",
    sidebarBg: "rgba(15, 23, 42, 0.95)",
    sidebarBorder: "rgba(255, 255, 255, 0.08)",
    text: "#ffffff",
    textSecondary: "rgba(255, 255, 255, 0.7)",
    navHover: "rgba(255, 255, 255, 0.08)",
    navActive: "linear-gradient(135deg, #2e7cba, #2e7cba)",
    topbarBg: "rgba(15, 23, 42, 0.95)",
    contentBg: "transparent",
    inputBg: "rgba(255, 255, 255, 0.08)",
    inputBorder: "rgba(255, 255, 255, 0.12)",
    shadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
    glassEffect: "blur(16px)",
    notificationBg: "rgba(15, 23, 42, 0.98)",
    notificationShadow: "0 20px 60px rgba(0, 0, 0, 0.6)",
    modalBg: "rgba(15, 23, 42, 0.98)",
    modalOverlay: "rgba(0, 0, 0, 0.6)",
    userProfileBg: "rgba(15, 23, 42, 0.98)",
    userProfileShadow: "0 20px 60px rgba(0, 0, 0, 0.6)",
    statusOnline: "#22c55e",
    statusOffline: "#64748b",
    statusAway: "#f59e0b",
    statusBusy: "#ef4444",
    scrollTrack: "rgba(30, 41, 59, 0.6)",
    scrollThumb:
      "linear-gradient(135deg, rgba(99, 102, 241, 0.4), rgba(59, 130, 246, 0.3))",
    scrollThumbHover:
      "linear-gradient(135deg, rgba(99, 102, 241, 0.6), rgba(59, 130, 246, 0.5))",
    scrollThumbActive:
      "linear-gradient(135deg, rgba(99, 102, 241, 0.8), rgba(59, 130, 246, 0.7))",
  },
};

// ===== RESPONSIVE HELPER FUNCTIONS =====
export const getResponsiveBadgeSize = () => {
  const screenWidth = window.innerWidth;
  if (screenWidth <= 480) return 12;
  if (screenWidth <= 768) return 14;
  if (screenWidth <= 1024) return 15;
  return 16;
};

export const getResponsiveFontSizes = (isMobile, isTablet) => {
  return {
    title: isMobile ? "1.125rem" : isTablet ? "1.25rem" : "1.375rem",
    body: isMobile ? "0.875rem" : isTablet ? "0.9375rem" : "0.9375rem",
    small: isMobile ? "0.75rem" : isTablet ? "0.8125rem" : "0.8125rem",
    tiny: isMobile ? "0.6875rem" : "0.75rem",
  };
};

export const getResponsiveSpacing = (isMobile, isTablet) => {
  return {
    padding: isMobile ? "0.75rem" : isTablet ? "1rem" : "1.25rem",
    margin: isMobile ? "0.5rem" : isTablet ? "0.75rem" : "1rem",
    gap: isMobile ? "0.5rem" : isTablet ? "0.75rem" : "1rem",
  };
};

// ===== STYLES SYSTEM =====
// c = sidebarColors[theme]
export const getSidebarStyles = ({
  c,
  theme,
  isRTL,
  isMobile,
  isTablet,
  isCollapsed,
  sidebarOpen,
  showUserProfile,
  fontSizes,
  spacing,
}) => {
  const sidebarWidthPx = isMobile
    ? sidebarOpen
      ? "min(280px, 80vw)"
      : "0"
    : isCollapsed
    ? "4.5rem"
    : isTablet
    ? "18rem"
    : "17.5rem";

  // Mobile gets the compact bar — the tall 6.1rem variant wastes a third of
  // a phone's above-the-fold space.
  const topbarHeight = isMobile || isCollapsed || isTablet ? "4.5rem" : "6.1rem";

  return {
    wrapper: {
      display: "flex",
      width: "100%",
      height: "100vh",
      background: c.wrapperBg,
      transition: "all 0.3s ease",
      overflow: "hidden",
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },

    sidebar: {
      position: "fixed",
      left: isRTL ? "auto" : isMobile ? (sidebarOpen ? "0" : "-100%") : "0",
      right: isRTL
        ? isMobile
          ? sidebarOpen
            ? "0"
            : "-100%"
          : "0"
        : "auto",
      top: "0",
      width: sidebarWidthPx,
      height: "100vh",
      background: c.sidebarBg,
      backdropFilter: c.glassEffect,
      border: `1px solid ${c.sidebarBorder}`,
      borderLeft: isRTL ? undefined : "none",
      borderRight: isRTL ? "none" : undefined,
      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
      zIndex: 1000,
      display: "flex",
      flexDirection: "column",
      boxShadow: c.shadow,
      overflow: "hidden",
      direction: isRTL ? "rtl" : "ltr",
    },

    sidebarHeader: {
      minHeight: "60px",
      borderBottom: `1px solid ${c.sidebarBorder}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      flexDirection: isRTL ? "row-reverse" : "row",
      gap: "0.2rem",
      background:
        "linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(99, 102, 241, 0.05))",
      flexShrink: 0,
      padding: "0.25rem 1rem",
    },

    logo: {
      width: isMobile ? "2rem" : isTablet ? "2.125rem" : "2.25rem",
      height: isMobile ? "2rem" : isTablet ? "2.125rem" : "2.25rem",
      backgroundColor: "transparent",
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },

    nav: {
      flex: 1,
      padding: `${spacing.margin} 0`,
      overflowY: "auto",
      overflowX: "hidden",
    },

    navItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: isCollapsed && !isMobile ? "center" : "flex-start",
      gap:
        isCollapsed && !isMobile ? "0" : isMobile ? "0.75rem" : spacing.gap,
      padding:
        isCollapsed && !isMobile
          ? "1rem 0.3rem"
          : `${spacing.margin} ${spacing.padding}`,
      margin:
        isCollapsed && !isMobile
          ? `0 0.75rem 0.75rem 0.75rem`
          : `0 ${spacing.margin} 0.625rem ${spacing.margin}`,
      color: c.textSecondary,
      cursor: "pointer",
      fontSize: fontSizes.body,
      fontWeight: "500",
      borderRadius: isMobile ? "0.75rem" : "1rem",
      transition:
        isCollapsed && !isMobile
          ? "background 0.3s ease, color 0.3s ease"
          : "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      position: "relative",
      border: "1px solid transparent",
      minHeight: "3rem",
      overflow: "hidden",
    },

    navItemActive: {
      background: c.navActive,
      color: "white",
      fontWeight: "600",
      transform:
        isCollapsed && !isMobile
          ? "scale(1.05)"
          : isMobile
          ? "translateX(0.25rem) scale(1.01)"
          : isTablet
          ? "translateX(0.5rem) scale(1.015)"
          : "translateX(0.5rem) scale(1.02)",
      boxShadow: "0 0.5rem 1.5625rem rgba(59, 130, 246, 0.3)",
      border: "1px solid rgba(255, 255, 255, 0.1)",
    },

    navItemText: {
      opacity: isCollapsed && !isMobile ? 0 : 1,
      transform:
        isCollapsed && !isMobile ? "translateX(-20px)" : "translateX(0)",
      transition: "all 0.3s ease",
      whiteSpace: "nowrap",
      overflow: "hidden",
    },

    main: {
      flex: 1,
      marginLeft: isRTL ? "0" : isMobile ? "0" : sidebarWidthPx,
      marginRight: isRTL ? (isMobile ? "0" : sidebarWidthPx) : "0",
      height: "100vh",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      transition: "margin 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
      direction: isRTL ? "rtl" : "ltr",
      position: "relative",
    },

    topbar: {
      background: c.topbarBg,
      backdropFilter: c.glassEffect,
      border: `1px solid ${c.sidebarBorder}`,
      borderRadius: isMobile ? "0" : "0 0 1.25rem 1.25rem",
      margin: isMobile ? "0" : "0 1rem",
      padding: isMobile
        ? `${spacing.padding} ${spacing.margin}`
        : isTablet
        ? `${spacing.padding} 1.5rem`
        : `${spacing.padding} 2rem`,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      zIndex: 100,
      boxShadow:
        "0 4px 12px rgba(0, 0, 0, 0.03), 0 8px 24px rgba(59, 130, 246, 0.05), 0 16px 40px rgba(0, 0, 0, 0.02)",
      flexShrink: 0,
      minHeight: topbarHeight,
      direction: isRTL ? "rtl" : "ltr",
    },

    content: {
      flex: 1,
      background: c.contentBg,
      padding: isMobile ? spacing.margin : spacing.padding,
      color: c.text,
      transition: "all 0.3s ease",
      overflow: "auto",
      height: `calc(100vh - ${topbarHeight})`,
      position: "relative",
      paddingTop: isMobile ? "1rem" : "1.5rem",
    },

    topbarLeft: {
      display: "flex",
      alignItems: "center",
      gap: isMobile ? "0.75rem" : isTablet ? "0.85rem" : "1rem",
      flex: "0 0 auto",
      order: 1,
      flexDirection: isRTL ? "row-reverse" : "row",
    },

    menuBtn: {
      background: c.inputBg,
      border: `1px solid ${c.inputBorder}`,
      color: c.text,
      cursor: "pointer",
      padding: isMobile ? "0.75rem" : "0.55rem",
      borderRadius: isMobile ? "0.75rem" : "8.875rem",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backdropFilter: "blur(0.625rem)",
      boxShadow: "0 0.25rem 0.75rem rgba(0, 0, 0, 0.05)",
      flexShrink: 0,
    },

    topbarRight: {
      display: "flex",
      alignItems: "center",
      gap: isMobile ? "0.5rem" : "1.125rem",
      color: c.text,
      flex: "0 0 auto",
      order: 2,
      flexDirection: isRTL ? "row-reverse" : "row",
    },

    themeToggle: {
      background: c.inputBg,
      border: `1px solid ${c.inputBorder}`,
      borderRadius: isMobile ? "0.75rem" : "0.875rem",
      padding: isMobile ? "0.75rem" : "0.75rem",
      cursor: "pointer",
      color: c.text,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.3s ease",
      backdropFilter: "blur(0.625rem)",
      boxShadow: "0 0.25rem 0.75rem rgba(0, 0, 0, 0.05)",
    },

    userInfo: {
      display: "flex",
      alignItems: "center",
      gap: isMobile ? "0.5rem" : "0.875rem",
      color: c.text,
      padding: isMobile ? "0.5rem 0.75rem" : "0.625rem 0.875rem",
      borderRadius: isMobile ? "0.75rem" : "0.875rem",
      transition: "all 0.3s ease",
      cursor: "pointer",
      background: c.inputBg,
      border: `1px solid ${c.inputBorder}`,
      backdropFilter: "blur(0.625rem)",
      boxShadow: "0 0.25rem 0.75rem rgba(0, 0, 0, 0.05)",
      position: "relative",
    },

    userAvatar: {
      width: isMobile ? "1.75rem" : "2.625rem",
      height: isMobile ? "1.75rem" : "2.625rem",
      borderRadius: "50%",
      objectFit: "cover",
      border: "2px solid rgba(59, 130, 246, 0.3)",
      transition: "all 0.3s ease",
      position: "relative",
    },

    userAvatarStatus: {
      position: "absolute",
      bottom: "0.125rem",
      right: "0.125rem",
      width: isMobile ? "0.625rem" : "0.75rem",
      height: isMobile ? "0.625rem" : "0.75rem",
      borderRadius: "50%",
      border: "2px solid white",
      background: c.statusOnline,
    },

    userDetails: {
      display: isMobile ? "none" : "flex",
      flexDirection: "column",
      gap: "0.125rem",
    },

    userName: {
      fontSize: fontSizes.body,
      fontWeight: "600",
      color: c.text,
    },

    userDropdownIcon: {
      color: c.textSecondary,
      transition: "all 0.3s ease",
      transform: showUserProfile ? "rotate(180deg)" : "rotate(0deg)",
      display: isMobile ? "none" : "block",
    },

    userProfileDropdown: {
      position: "fixed",
      top: isMobile ? topbarHeight : "6.25rem",
      right: isRTL ? "auto" : isMobile ? "0.5rem" : "6.25rem",
      left: isRTL ? (isMobile ? "0.5rem" : "6.25rem") : "auto",
      width: isMobile
        ? "calc(100vw - 1rem)"
        : isTablet
        ? "25rem"
        : "31.25rem",
      background: c.userProfileBg,
      backdropFilter: "blur(1.25rem)",
      border: `1px solid ${c.sidebarBorder}`,
      borderRadius: isMobile ? "1rem" : "1.25rem",
      boxShadow: c.userProfileShadow,
      zIndex: 1001,
      opacity: showUserProfile ? 1 : 0,
      visibility: showUserProfile ? "visible" : "hidden",
      transform: showUserProfile
        ? "translateY(0) scale(1)"
        : "translateY(-0.625rem) scale(0.95)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      maxHeight: "80vh",
      overflowY: "auto",
      direction: isRTL ? "rtl" : "ltr",
    },

    userProfileHeader: {
      padding: isMobile ? "1.25rem" : "1.5rem",
      borderBottom: `1px solid ${c.sidebarBorder}`,
      background:
        "linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(99, 102, 241, 0.02))",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    },

    userProfileAvatar: {
      width: isMobile ? "4rem" : "5.75rem",
      height: isMobile ? "4rem" : "5.75rem",
      borderRadius: "50%",
      objectFit: "cover",
      border: "2px solid rgba(59, 130, 246, 0.3)",
      transition: "all 0.3s ease",
      marginBottom: "0.625rem",
    },

    userProfileName: {
      fontSize: isMobile ? "1.125rem" : "1.25rem",
      fontWeight: "700",
      color: c.text,
      textAlign: "center",
      marginBottom: "0.375rem",
    },

    userProfileStatus: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "0.5rem",
      marginTop: "0.75rem",
    },

    userProfileStatusDot: {
      width: "0.625rem",
      height: "0.625rem",
      borderRadius: "50%",
      background: c.statusOnline,
    },

    userProfileStatusText: {
      fontSize: fontSizes.small,
      color: c.textSecondary,
      fontWeight: "500",
    },

    userProfileContent: {
      padding: "1rem 0",
    },

    userProfileSection: {
      padding: isMobile ? "0.75rem 1.25rem" : "0.75rem 1.5rem",
    },

    userProfileSectionTitle: {
      fontSize: fontSizes.small,
      fontWeight: "600",
      color: c.textSecondary,
      textTransform: "uppercase",
      letterSpacing: "0.03125rem",
      marginBottom: "0.75rem",
    },

    userProfileItem: {
      display: "flex",
      alignItems: "center",
      gap: "0.75rem",
      padding: "0.625rem 0",
      fontSize: fontSizes.body,
      color: c.text,
    },

    userProfileItemIcon: {
      color: "#2e7cba",
      flexShrink: 0,
    },

    logoutBtn: {
      background: "linear-gradient(135deg, #ef4444, #dc2626)",
      color: "white",
      border: "none",
      padding: isMobile ? "0.75rem 1rem" : "0.75rem 0.955rem",
      borderRadius: isMobile ? "0.75rem" : "9.875rem",
      cursor: "pointer",
      fontSize: fontSizes.body,
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "0.625rem",
      transition: "all 0.3s ease",
      height: isMobile ? "2.75rem" : "2.85rem",
    },

    switchbtn: {
      background: "linear-gradient(135deg, #2e7cba, #2e7cba)",
      color: "white",
      border: "none",
      padding: isMobile ? "0.75rem 1rem" : "0.75rem 0.955rem",
      borderRadius: isMobile ? "0.75rem" : "9.875rem",
      cursor: "pointer",
      fontSize: fontSizes.body,
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "0.625rem",
    },

    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.4)",
      zIndex: 999,
      opacity: sidebarOpen && isMobile ? 1 : 0,
      visibility: sidebarOpen && isMobile ? "visible" : "hidden",
      transition: "all 0.3s ease",
      backdropFilter: "blur(0.25rem)",
    },
  };
};

// ===== GLOBAL CSS (injected once by the sidebar) =====
export const getSidebarGlobalCss = ({ c, theme, isMobile, isTablet, isCollapsed }) => {
  // Must mirror the topbarHeight formula in getSidebarStyles above.
  const topbarHeight = isMobile || isCollapsed || isTablet ? "4.5rem" : "6.1rem";
  return `
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html, body {
    width: 100%;
    height: 100vh;
    overflow: hidden;
    background: transparent !important;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  #root {
    width: 100%;
    height: 100vh;
    overflow: hidden;
    background: transparent !important;
  }

  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.15); opacity: 0.8; }
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(0.625rem); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes slideInLeft {
    from { transform: translateX(-100%); }
    to { transform: translateX(0); }
  }

  .logo:hover {
    transform: scale(${isMobile ? "1.05" : "1.1"}) rotate(5deg) !important;
    box-shadow: 0 0 1.5625rem rgba(59, 130, 246, 0.6) !important;
  }

  .menu-btn:hover {
    background: ${c.navHover} !important;
    transform: scale(${isMobile ? "1.05" : "1.1"}) !important;
  }

  .collapse-btn:hover {
    background: ${c.navHover} !important;
    transform: scale(1.1) !important;
    box-shadow: 0 0.25rem 0.75rem rgba(59, 130, 246, 0.2) !important;
  }

  .theme-toggle:hover {
    background: ${c.navHover} !important;
    transform: scale(${isMobile ? "1.05" : "1.1"}) !important;
  }

  .notification-btn:hover {
    background: ${c.navHover} !important;
    transform: scale(${isMobile ? "1.05" : "1.1"}) !important;
  }

  .user-info:hover {
    background: ${c.navHover} !important;
    transform: scale(${isMobile ? "1.01" : "1.02"}) !important;
  }

  .nav-item:hover {
    background: ${c.navHover} !important;
    color: ${c.text} !important;
    transform: ${
      isCollapsed && !isMobile
        ? "scale(1.05)"
        : `translateX(${isMobile ? "0.25rem" : "0.5rem"}) scale(${
            isMobile ? "1.01" : "1.02"
          })`
    } !important;
  }

  /* Hide the nav scrollbar */
  .nav::-webkit-scrollbar {
    display: none !important;
  }

  .nav {
    scrollbar-width: none !important; /* Firefox */
    -ms-overflow-style: none !important; /* IE and Edge */
  }

  .content::-webkit-scrollbar,
  .user-profile-dropdown::-webkit-scrollbar {
    display: none !important;
  }

  .content,
  .user-profile-dropdown {
    scrollbar-width: none !important;
    -ms-overflow-style: none !important;
  }

  @media (max-width: 480px) {
    .user-details {
      display: none !important;
    }
    .date-time-display {
      display: none !important;
    }
    .logout-text {
      display: none !important;
    }
    .user-dropdown-icon {
      display: none !important;
    }
  }

  @media (min-width: 481px) and (max-width: 768px) {
    .user-details {
      display: none !important;
    }
    .date-time-display {
      display: none !important;
    }
  }

  @media (min-width: 769px) and (max-width: 1024px) {
    .date-time-display {
      display: flex !important;
    }
    .user-details {
      display: flex !important;
    }
  }

  .fade-in {
    animation: fadeIn 0.4s ease-out;
  }

  .slide-in-left {
    animation: slideInLeft 0.4s ease-out;
  }

  @media (pointer: coarse) {
    button, .nav-item, .user-info {
      min-height: 2.75rem !important;
      min-width: 2.75rem !important;
    }
  }

  @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
    .logo img, .user-avatar, .user-profile-avatar {
      image-rendering: -webkit-optimize-contrast;
      image-rendering: crisp-edges;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }

  @media (prefers-color-scheme: dark) {
    :root {
      color-scheme: dark;
    }
  }

  @media (prefers-color-scheme: light) {
    :root {
      color-scheme: light;
    }
  }

  .profile-upload-btn:hover {
    transform: scale(1.1) !important;
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.6) !important;
  }

  .profile-upload-btn:active {
    transform: scale(0.95) !important;
  }

  .profile-upload-btn:disabled {
    cursor: not-allowed !important;
    opacity: 0.6 !important;
  }

  /* Dynamic-viewport heights: on mobile browsers the URL/tool bars overlay
     part of 100vh, hiding bottom content. Prefer dvh where supported —
     !important because the shell's inline styles set the vh values. */
  @supports (height: 100dvh) {
    html, body, #root { height: 100dvh; }
    .app-shell-root, .app-shell-main { height: 100dvh !important; }
    .app-sidebar { height: 100dvh !important; }
    .content { height: calc(100dvh - ${topbarHeight}) !important; }
  }

  /* Compact topbar controls on small phones so the right cluster
     (switcher + theme + bell + avatar) never overflows the bar. */
  @media (max-width: 400px) {
    .view-type-switcher { padding: 3px !important; gap: 2px !important; }
    .view-type-switcher button { padding: 7px 9px !important; }
  }
`;
};
